import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { interval, map, of, startWith } from 'rxjs';
import { PermissionService, UsersMeService } from '@zag/auth/ui';
import { FormattingService, ToastService, ZagIcon } from '@zag/ui';
import type { ClockState } from '@zag/timetracking/common';
import { ClockStampService } from './clock-stamp.service';
import { ClockStampEventsService } from './clock-stamp-events.service';

/**
 * Wall-clock "now" encoded as UTC — the ledger convention of the stamp
 * timestamps (a 08:00 local clock-in is stored as 08:00Z). Comparing against
 * `Date.now()` would shift the runtime by the UTC offset.
 */
export function wallClockNowMs(): number {
  return Date.now() - new Date().getTimezoneOffset() * 60_000;
}

/** `H:mm` runtime since clock-in; empty when the elapsed time is not measurable. */
export function elapsedLabel(startedAtIso: string, nowMs: number): string {
  const startedMs = new Date(startedAtIso).getTime();
  if (Number.isNaN(startedMs) || nowMs < startedMs) return '';
  const totalMinutes = Math.floor((nowMs - startedMs) / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Permanent topbar quick-stamp button (F12, story 31-1): shows the clock
 * state (play icon = clocked out, stop icon + subtle runtime = clocked in —
 * PO feedback round 1, spec §3a-1), toggles on click with an optimistic
 * update + toast, and carries the last stamp in its tooltip. Rendered only
 * for users with the timetracking permission (gated by the layout) AND a
 * linked person (gated here).
 */
@Component({
  selector: 'sc-clock-stamp-button',
  imports: [TranslatePipe, ZagIcon],
  template: `
    @if (visible()) {
      <button
        type="button"
        class="clock-stamp"
        [class.clock-stamp--in]="isClockedIn()"
        [disabled]="!canStamp() || mutating()"
        [attr.aria-label]="actionLabel()"
        [attr.title]="tooltip()"
        (click)="toggle()"
      >
        <span [zagIcon]="isClockedIn() ? 'stop' : 'play_arrow'"></span>
        @if (isClockedIn() && runtime()) {
          <span class="clock-stamp__runtime" aria-hidden="true">{{ runtime() }}</span>
        }
      </button>
    }
  `,
  styles: [
    `
      .clock-stamp {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        height: 36px;
        padding: 0 var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-md);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition: all var(--transition-duration-fast);
        flex-shrink: 0;

        &:hover:not(:disabled) {
          background: var(--surface-color-hover);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        span[zagIcon] {
          font-size: 20px;
        }
      }

      .clock-stamp--in {
        color: var(--color-success, var(--color-primary));
      }

      .clock-stamp__runtime {
        font-size: var(--font-size-xs);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      /* The topbar hides secondary actions on the smallest viewports (same as the AI button). */
      @media (max-width: 539px) {
        .clock-stamp {
          display: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClockStampButtonComponent {
  private readonly clockService = inject(ClockStampService);
  private readonly stampEvents = inject(ClockStampEventsService);
  private readonly usersMeService = inject(UsersMeService);
  private readonly permissions = inject(PermissionService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly formatting = inject(FormattingService);

  protected readonly mutating = signal(false);
  /** Server truth after the last mutation — wins over the initial resource load. */
  private readonly overrideState = signal<ClockState | null>(null);

  private readonly hasLinkedPerson = computed(() => this.usersMeService.profile()?.linkedPerson?.id !== undefined);
  protected readonly canStamp = computed(() => this.permissions.hasPermission('timetracking:my-week:confirm'));

  private readonly stateResource = rxResource<ClockState | null, boolean>({
    params: () => this.hasLinkedPerson(),
    stream: ({ params: linked }) => (linked ? this.clockService.getState() : of(null)),
  });

  // hasValue() guard: value() rethrows while the resource is in the error
  // state — the failed initial load must render as "state unknown", not break
  // change detection.
  protected readonly state = computed<ClockState | null>(
    () => this.overrideState() ?? (this.stateResource.hasValue() ? (this.stateResource.value() ?? null) : null)
  );
  /**
   * Permanent for linked persons (AC-A1) — a failed initial state load must
   * not hide the button for the whole session; a click then retries the load.
   */
  protected readonly visible = computed(() => this.hasLinkedPerson());
  protected readonly isClockedIn = computed(() => this.state()?.isClockedIn ?? false);

  /** Minute tick for the subtle runtime display (wall-clock-as-UTC, like the stamps). */
  private readonly nowMs = toSignal(interval(60_000).pipe(startWith(0), map(wallClockNowMs)), { initialValue: wallClockNowMs() });

  protected readonly runtime = computed(() => {
    const startedAt = this.state()?.openStamp?.startedAt;
    return startedAt ? elapsedLabel(startedAt, this.nowMs()) : '';
  });

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly actionLabel = computed(() => {
    this.currentLang();
    return this.translate.instant(this.isClockedIn() ? 'clockStamp.clockOut' : 'clockStamp.clockIn');
  });

  protected readonly tooltip = computed(() => {
    this.currentLang();
    const last = this.state()?.lastStamp;
    if (!last) return this.actionLabel();
    const at = new Date(last.at);
    // timeZone UTC: stamp times are wall clock encoded as UTC (ledger convention).
    const lastLine = this.translate.instant(last.type === 'in' ? 'clockStamp.lastStampIn' : 'clockStamp.lastStampOut', {
      date: this.formatting.formatDate(at, { timeZone: 'UTC' }),
      time: this.formatting.formatTime(at, { timeZone: 'UTC' }),
    });
    return `${this.actionLabel()} — ${lastLine}`;
  });

  protected toggle(): void {
    if (this.mutating()) return;
    // No known state (initial load failed or still pending): retry the load
    // instead of stamping blind — the user might already be clocked in.
    if (this.state() === null) {
      if (this.stateResource.error() !== undefined) this.stateResource.reload();
      return;
    }
    const clockedIn = this.isClockedIn();
    this.mutating.set(true);
    // Optimistic flip — the server response replaces it in both outcomes.
    this.overrideState.set(optimisticToggle(this.state(), clockedIn));

    const request = clockedIn ? this.clockService.clockOut() : this.clockService.clockIn();
    request.subscribe({
      next: state => {
        this.mutating.set(false);
        this.overrideState.set(state);
        this.toast.success(this.resolveSuccessToast(clockedIn, state));
        // Story 35-1 (F16a): the week view refreshes on every stamp change.
        this.stampEvents.notifyStampChanged();
      },
      error: (error: unknown) => {
        this.mutating.set(false);
        this.overrideState.set(null);
        this.stateResource.reload();
        if (error instanceof HttpErrorResponse && error.status === 409) {
          this.toast.error(this.translate.instant(clockedIn ? 'clockStamp.conflictOut' : 'clockStamp.conflictIn'));
        } else {
          this.toast.error(this.translate.instant('clockStamp.error'));
        }
      },
    });
  }

  /**
   * Success toast with the stamp time (story 35-1, F16a: "Eingestempelt um
   * HH:MM" — the clock-in confirms itself like the clock-out already did).
   * timeZone UTC: stamp times are wall clock encoded as UTC.
   */
  private resolveSuccessToast(wasClockedIn: boolean, state: ClockState): string {
    if (wasClockedIn) return this.translate.instant('clockStamp.clockedOut');
    const startedAt = state.openStamp?.startedAt;
    if (!startedAt) return this.translate.instant('clockStamp.clockedIn');
    return this.translate.instant('clockStamp.clockedInAt', {
      time: this.formatting.formatTime(new Date(startedAt), { timeZone: 'UTC' }),
    });
  }
}

/** Local optimistic state until the server responds (wall-clock-as-UTC timestamps). */
export function optimisticToggle(state: ClockState | null, wasClockedIn: boolean): ClockState {
  const nowIso = new Date(wallClockNowMs()).toISOString();
  if (wasClockedIn) {
    return { isClockedIn: false, openStamp: null, lastStamp: { type: 'out', at: nowIso } };
  }
  return {
    isClockedIn: true,
    openStamp: { bookingId: state?.openStamp?.bookingId ?? '', startedAt: nowIso },
    lastStamp: { type: 'in', at: nowIso },
  };
}
